/**
 * HOST SPA mount for APPLICATION:THEG_MOVIES_LIBRARY.
 * Wires ThegMoviesShell + /theg-movies/* client routes into the Vite entry
 * without replacing enterprise Mrk|New|Apps|Exp|Sett on non-THEG paths.
 * Member/project/infra resolve via RUNYI assignments only — never hardcoded.
 * AGENT:GROK_BOT — HOST_SPA_MOUNT 2026-10-01
 */
"use client";

import { useEffect, useMemo, useState } from "react";
import { ThegMoviesShell } from "./theg-movies-library/ThegMoviesShell";
import {
  THEG_MOVIES_LIBRARY_BOTTOM_NAV,
  assertThegFiveTabs,
  type ThegMoviesNavTabId,
} from "./theg-movies-library/theg-movies-library-nav-registry";

assertThegFiveTabs();

const TAB_BY_ID = new Map(
  THEG_MOVIES_LIBRARY_BOTTOM_NAV.map((tab) => [tab.id, tab] as const),
);

export function isThegMoviesPath(pathname: string): boolean {
  return pathname === "/theg-movies" || pathname.startsWith("/theg-movies/");
}

function parseThegTab(pathname: string): ThegMoviesNavTabId {
  const match = pathname.match(/^\/theg-movies\/([^/]+)\/?$/);
  const id = (match?.[1] ?? "home") as ThegMoviesNavTabId;
  return TAB_BY_ID.has(id) ? id : "home";
}

function ThegWorkspaceStub({ tabId }: { tabId: ThegMoviesNavTabId }) {
  const tab = TAB_BY_ID.get(tabId)!;
  return (
    <section className="space-y-3 p-4" data-theg-workspace={tab.id}>
      <h1 className="text-lg font-semibold tracking-tight">{tab.mobileLabel}</h1>
      <p className="text-sm text-foreground/60">
        APPLICATION:THEG_MOVIES_LIBRARY — {tab.mobileLabel} workspace. Host SPA
        mount for Vite client routes under /theg-movies/*. Member assignment
        resolves via RUNYI (universal — not hardcoded). Movie DNA untouched.
      </p>
    </section>
  );
}

export function ThegMoviesHostMount() {
  const [pathname, setPathname] = useState(() =>
    typeof window !== "undefined" ? window.location.pathname : "/theg-movies/home",
  );

  useEffect(() => {
    const sync = () => setPathname(window.location.pathname || "/theg-movies/home");
    window.addEventListener("popstate", sync);
    return () => window.removeEventListener("popstate", sync);
  }, []);

  useEffect(() => {
    if (pathname === "/theg-movies" || pathname === "/theg-movies/") {
      window.history.replaceState({}, "", "/theg-movies/home");
      setPathname("/theg-movies/home");
    }
  }, [pathname]);

  const tabId = useMemo(() => parseThegTab(pathname), [pathname]);

  return (
    <ThegMoviesShell>
      <ThegWorkspaceStub tabId={tabId} />
    </ThegMoviesShell>
  );
}
